import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Link,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import PhoneIcon          from '@mui/icons-material/Phone';
import WhatsAppIcon       from '@mui/icons-material/WhatsApp';
import LanguageIcon       from '@mui/icons-material/Language';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EditIconSvg from '../../../../components/common/EditIconSvg';
import PersonAddIcon      from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon   from '@mui/icons-material/PersonRemove';
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline';
import { useLeadDetails } from '../../../../contexts/LeadDetailsContext';
import AddNotes        from '../../../../components/common/AddNotes';
import EditLeadContact from '../EditLeadContact';

// ─── Design tokens (matching HTML file exactly) ───────────────────────────────
const C = {
  teal:        '#0E9F7E',
  tealLight:   '#ECFDF5',
  tealMid:     '#34D399',
  tealDark:    '#065F46',
  slate:       '#0F172A',
  slate2:      '#1E293B',
  slate3:      '#64748B',
  slate4:      '#94A3B8',
  border:      '#E2E8F0',
  borderSubtle:'#F1F5F9',
  bg:          '#F8FAFC',
  bg2:         '#F1F5F9',
  white:       '#FFFFFF',
  red:         '#EF4444',
  redLight:    '#FEF2F2',
  amber:       '#D97706',
  amberLight:  '#FFFBEB',
  blue:        '#2563EB',
  blueLight:   '#EFF6FF',
  purple:      '#7C3AED',
  purpleLight: '#F5F3FF',
};

const radius = { xs: '6px', sm: '8px', md: '10px', lg: '14px' };

// ─── Field card row (bg panel with label + value) ─────────────────────────────
const FieldCard = ({ label, children }) => (
  <Box
    sx={{
      bgcolor: C.bg,
      borderRadius: radius.sm,
      p: '8px 10px',
      mb: 1.5,
    }}
  >
    <Typography
      sx={{
        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.05em', color: C.slate4, mb: '3px',
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ContactDetails = React.memo(({
  contactInfo,
  leadId,
  lead,
  defaultFilters,
  onLeadUpdate,
  openTaskModal,
  setOpenTaskModal,
}) => {
  const [openNoteModal, setOpenNoteModal]                       = useState(false);
  const [openEditModal, setOpenEditModal]                       = useState(false);
  const [channelPartnerMenuAnchor, setChannelPartnerMenuAnchor] = useState(null);
  const [channelPartnerLoading, setChannelPartnerLoading]       = useState(false);
  const {
    fetchLeadNotes,
    fetchLeadDetails,
    assignLeadChannelPartner,
    removeLeadChannelPartner,
  } = useLeadDetails();

  const { channel_partner } = lead || {};
  const isLeadDeleted = Boolean(lead?.is_deleted);
  const isJunkLead = String(lead?.stage || '').toLowerCase().replace(/\s+/g, '_').trim() === 'junk';
  const contactEditsLocked = isLeadDeleted || isJunkLead;

  // ── helpers ────────────────────────────────────────────────────────────────
  const getInitials = (name = '') =>
    name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'L';

  // ── channel partner handlers ───────────────────────────────────────────────
  const openMenu  = (e) => {
    if (contactEditsLocked) return;
    setChannelPartnerMenuAnchor(e.currentTarget);
  };
  const closeMenu = ()  => setChannelPartnerMenuAnchor(null);

  const handleAssign = async (partnerId) => {
    try {
      setChannelPartnerLoading(true);
      const found = defaultFilters?.channel_partner?.find((p) => p.value === partnerId);
      if (found && onLeadUpdate)
        onLeadUpdate({ ...lead, channel_partner: { id: partnerId, name: found.label } });
      const res = await assignLeadChannelPartner(leadId, { channel_partner_id: partnerId });
      if (!(res.data?.success || res.status === 200) && onLeadUpdate) onLeadUpdate(lead);
    } catch {
      if (onLeadUpdate) onLeadUpdate(lead);
    } finally {
      setChannelPartnerLoading(false);
      closeMenu();
    }
  };

  const handleRemove = async () => {
    try {
      setChannelPartnerLoading(true);
      if (onLeadUpdate) onLeadUpdate({ ...lead, channel_partner: null });
      const res = await removeLeadChannelPartner(leadId);
      if (!(res.data?.success || res.status === 200) && onLeadUpdate) onLeadUpdate(lead);
    } catch {
      if (onLeadUpdate) onLeadUpdate(lead);
    } finally {
      setChannelPartnerLoading(false);
      closeMenu();
    }
  };

  // ── no contact fallback ────────────────────────────────────────────────────
  if (!contactInfo) {
    return (
      <Box sx={{ p: '18px 16px 14px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontSize: 12, color: C.slate3 }}>No contact information available</Typography>
      </Box>
    );
  }

  const name   = contactInfo.name   || '';
  const email  = contactInfo.email  || '';
  const mobile = contactInfo.mobile || '';
  const source = lead?.source_verbose || lead?.source || lead?.lead_source || '—';

  return (
    <>
      {/* ── sb-header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          p: '18px 16px 14px',
          borderBottom: `1px solid ${C.border}`,
          // background: 'linear-gradient(160deg, #F0FDF9 0%, #FFFFFF 100%)',
        }}
      >
        {/* Header row: "Contact Details" + edit pencil */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.slate2 }}>
            Contact Details
          </Typography>
          <IconButton
            size="small"
            disabled={contactEditsLocked}
            onClick={() => !contactEditsLocked && setOpenEditModal(true)}
            sx={{ width: 22, height: 22, color: C.slate4, '&:hover': { color: C.slate2, bgcolor: C.bg } }}
          >
            <EditIconSvg color={C.indigo} size={13} />
          </IconButton>
        </Box>

        {/* Avatar + name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 1.5 }}>
          <Avatar
            sx={{
              width: 44, height: 44, borderRadius: '12px',
              background: `linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)`,
              fontSize: 17, fontWeight: 700,
              boxShadow: '0 4px 12px rgba(14,159,126,0.3)',
              flexShrink: 0,
            }}
          >
            {getInitials(name)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.slate4, mb: '2px' }}>
              Name
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.slate, letterSpacing: '-0.2px' }}>
              {name || 'Lead'}
            </Typography>
          </Box>
        </Box>

        {/* Email */}
        {email && (
          <FieldCard label="Email">
            {isLeadDeleted ? (
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.slate2, display: 'block', wordBreak: 'break-all' }}>
                {email}
              </Typography>
            ) : (
              <Link
                href={`mailto:${email}`}
                underline="hover"
                sx={{ fontSize: 12, fontWeight: 500, color: C.slate2, display: 'block', wordBreak: 'break-all' }}
              >
                {email}
              </Link>
            )}
          </FieldCard>
        )}

        {/* Mobile */}
        {mobile && (
          <FieldCard label="Mobile">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <PhoneIcon sx={{ fontSize: 13, color: C.teal }} />
                {isLeadDeleted ? (
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.slate2 }}>{mobile}</Typography>
                ) : (
                  <Link
                    href={`tel:${mobile}`}
                    underline="hover"
                    sx={{ fontSize: 12, fontWeight: 500, color: C.slate2 }}
                  >
                    {mobile}
                  </Link>
                )}
              </Box>
            </Box>
          </FieldCard>
        )}

        {/* Lead Source */}
        <FieldCard label="Lead Source">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <Box
              sx={{
                width: 24, height: 24, borderRadius: '6px',
                bgcolor: C.blueLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <LanguageIcon sx={{ fontSize: 12, color: C.blue }} />
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.slate2 }}>{source}</Typography>
          </Box>
        </FieldCard>

        {/* Channel Partner */}
        <Box sx={{ bgcolor: C.bg, borderRadius: radius.sm, p: '8px 10px' }}>
          <Typography
            sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.slate4, mb: '5px' }}
          >
            Channel Partner
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <Box
              sx={{
                width: 24, height: 24, borderRadius: '6px',
                bgcolor: '#EDE9FE',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <BusinessCenterIcon sx={{ fontSize: 12, color: C.purple }} />
            </Box>
            <Typography
              sx={{
                fontSize: 12, fontWeight: 500,
                color: channel_partner ? C.slate2 : C.slate4,
                fontStyle: channel_partner ? 'normal' : 'italic',
              }}
            >
              {channel_partner ? channel_partner.name : 'Not assigned'}
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              {channel_partner ? (
                <IconButton
                  size="small"
                  onClick={openMenu}
                  disabled={contactEditsLocked || channelPartnerLoading}
                  sx={{ width: 24, height: 24, color: C.slate4, '&:hover': { color: C.teal, bgcolor: C.tealLight } }}
                >
                  {channelPartnerLoading ? <CircularProgress size={12} /> : <EditIconSvg sx={{ fontSize: 12, color: C.slate4 }} />}
                </IconButton>
              ) : (
                <Button
                  size="small"
                  onClick={openMenu}
                  disabled={contactEditsLocked}
                  sx={{
                    fontSize: '10px', fontWeight: 600, px: '7px', py: '2px',
                    borderRadius: '10px', bgcolor: C.white, color: C.teal,
                    border: `1px solid ${C.tealMid}`, textTransform: 'none', minWidth: 0,
                    '&:hover': { bgcolor: C.tealLight },
                  }}
                >
                  + Assign
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      <AddNotes
        open={openNoteModal}
        onClose={() => setOpenNoteModal(false)}
        leadIds={[parseInt(leadId)]}
        onSave={() => fetchLeadNotes(leadId)}
      />
      <EditLeadContact
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        lead={lead}
        source={defaultFilters?.source || {}}
        onComplete={async (updated) => {
          setOpenEditModal(false);
          try {
            if (leadId) {
              const fresh = await fetchLeadDetails(leadId);
              if (onLeadUpdate) onLeadUpdate(fresh);
            } else if (onLeadUpdate) {
              onLeadUpdate(updated);
            }
          } catch {
            if (onLeadUpdate) onLeadUpdate(updated);
          }
        }}
      />

      {/* ── Channel Partner Menu ──────────────────────────────────────────── */}
      <Menu
        anchorEl={channelPartnerMenuAnchor}
        open={Boolean(channelPartnerMenuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            minWidth: 240, maxHeight: 320, borderRadius: radius.sm,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: `1px solid ${C.border}`,
            '& .MuiMenuItem-root': {
              fontSize: 12, py: '6px', px: '12px', borderRadius: '6px', mx: '6px', my: '1px',
              '&:hover': { bgcolor: C.blueLight },
            },
          },
        }}
      >
        {channel_partner && (
          <>
            <Box sx={{ px: '12px', pt: '8px', pb: '4px' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.slate4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Current Assignment
              </Typography>
            </Box>
            <MenuItem disabled sx={{ opacity: '1 !important', bgcolor: `${C.blueLight} !important`, mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <BusinessCenterIcon sx={{ fontSize: 14, color: C.blue }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography sx={{ fontSize: 12, fontWeight: 600 }}>{channel_partner.name}</Typography>}
                secondary={<Chip label="Current" size="small" sx={{ height: 14, fontSize: '0.58rem', bgcolor: C.blue, color: '#fff', mt: 0.25, '& .MuiChip-label': { px: 0.6 } }} />}
              />
            </MenuItem>
            <Box sx={{ borderTop: `1px solid ${C.border}`, mx: '12px', my: '4px' }} />
          </>
        )}
        <Box sx={{ px: '12px', py: '4px' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.slate4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {channel_partner ? 'Reassign To' : 'Assign To'}
          </Typography>
        </Box>
        {defaultFilters?.channel_partner
          ?.filter((p) => !channel_partner || p.value !== channel_partner.id)
          ?.map((p) => (
            <MenuItem key={p.value} onClick={() => handleAssign(p.value)} disabled={channelPartnerLoading}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <PersonAddIcon sx={{ fontSize: 14, color: C.blue }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 12, fontWeight: 500 }}>{p.label}</Typography>} />
            </MenuItem>
          ))}
        {channel_partner && (
          <>
            <Box sx={{ borderTop: `1px solid ${C.border}`, mx: '12px', my: '4px' }} />
            <MenuItem
              onClick={handleRemove}
              disabled={channelPartnerLoading}
              sx={{ color: 'error.main', '&:hover': { bgcolor: '#FEF2F2 !important' } }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <PersonRemoveIcon sx={{ fontSize: 14, color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>Remove Channel Partner</Typography>}
                secondary={<Typography sx={{ fontSize: '0.68rem', color: 'error.light' }}>Unassign from this lead</Typography>}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
});

export default ContactDetails;